import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { useGetOrganizationQuery, useOrganizations } from 'features/organization';
import { useConfig } from 'hooks/use-config';
import { useMyPlan } from 'features/subscription/hooks/use-seats';
import { getRoleTranslationKey, ROLES } from 'constant/roles';
import { AccountRow } from './account-row';
import {
  accountBadgeClassName,
  accountOutlineButtonClassName,
  accountSectionClassName,
  accountSectionTitleClassName,
} from './account-styles';

interface WorkspaceCardProps {
  memberSince?: string;
}

export function WorkspaceCard({ memberSince }: WorkspaceCardProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useCommonStore((state) => state.user);
  const selectedOrgId = useAuthStore((state) => state.selectedOrgId);
  const { configurationSettings } = useConfig();
  const organizationId = selectedOrgId || user?.organizationId || '';
  const { data: organization } = useGetOrganizationQuery(organizationId);
  // Same params as the org switcher, so the list comes from the cache. The single-org request
  // returns nothing for the "default" org, but the list has its name.
  const { data: organizations } = useOrganizations({
    page: 0,
    pageSize: 50,
    enabled: Boolean(configurationSettings.blocksKey),
  });
  const organizationName =
    organizations?.data?.find((item) => item.itemId === organizationId)?.name ||
    organization?.name ||
    '-';
  const { place, isFreeAccess } = useMyPlan();

  // Highest first, same as the profile menu.
  const highestRole = [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.USER].find((role) =>
    user?.roles?.includes(role)
  );
  // Plans can only be changed by admins (the plans route is admin-only).
  const isAdmin = Boolean(user?.roles?.includes(ROLES.ADMIN));
  const planName = isFreeAccess
    ? t('UNLIMITED_USAGE', 'Unlimited usage allowed')
    : place?.planName || place?.planCode || '-';
  const memberSinceDate = memberSince ? new Date(memberSince) : null;
  const memberSinceLabel =
    memberSinceDate && !isNaN(memberSinceDate.getTime())
      ? memberSinceDate.toLocaleDateString(i18n.language, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : '-';

  return (
    <section className={cn(accountSectionClassName, 'px-6 pb-2 pt-5')}>
      <h2 className={accountSectionTitleClassName}>
        {t('ACCOUNT_YOUR_WORKSPACE', { defaultValue: 'Your workspace' })}
      </h2>

      <AccountRow
        title={organizationName}
        hint={t('ACCOUNT_MEMBER_SINCE', {
          defaultValue: 'Member since {{date}}',
          date: memberSinceLabel,
        })}
      >
        {highestRole ? (
          <span
            className={cn(
              accountBadgeClassName,
              'sm:ml-auto bg-grey-100 text-grey-600 dark:bg-muted dark:text-muted-foreground'
            )}
          >
            {t(getRoleTranslationKey(highestRole))}
          </span>
        ) : null}
      </AccountRow>

      <AccountRow
        title={t('ACCOUNT_YOUR_PLAN', { defaultValue: 'Your plan' })}
        hint={t('ACCOUNT_YOUR_PLAN_HINT', {
          defaultValue: 'Sets your own daily and monthly limits',
        })}
        isLast
      >
        <div className="flex min-h-10 w-full items-center justify-between gap-4 sm:justify-end">
          <span className="text-sm text-grey-950 dark:text-foreground">{planName}</span>
          {isAdmin ? (
            <button
              type="button"
              onClick={() => navigate('/subscription-plans')}
              className={accountOutlineButtonClassName}
            >
              {t('ACCOUNT_VIEW_PLANS', { defaultValue: 'View plans' })}
            </button>
          ) : null}
        </div>
      </AccountRow>
    </section>
  );
}
